import React, { useEffect, useMemo, useState } from "react";
import { ApiHttpError, fetchPnlV2 } from "../api";
import type {
  PnlByHorizonRow,
  PnlByStrategyRow,
  PnlBySymbolRow,
  PnlEquityCurveRow,
  PnlResponse,
} from "../types";

function num(v: string | number | null | undefined): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function int(v: string | number | null | undefined): string {
  const n = num(v);
  return n == null ? "—" : Math.round(n).toLocaleString("en-US");
}

function usd(v: string | number | null | undefined): string {
  const n = num(v);
  if (n == null) return "—";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

function bps(v: string | number | null | undefined): string {
  const n = num(v);
  if (n == null) return "—";
  return `${n >= 0 ? "+" : ""}${n.toFixed(1)} bps`;
}

function pct(fraction: string | number | null | undefined): string {
  const n = num(fraction);
  if (n == null) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function pnlClass(v: string | number | null | undefined): string {
  const n = num(v);
  if (n == null || n === 0) return "mono";
  return n > 0 ? "mono pos" : "mono neg";
}

function EquitySparkline({ rows }: { rows: PnlEquityCurveRow[] }) {
  // rows arrive DESC by date; chart oldest -> newest left to right.
  const points = useMemo(() => {
    return [...rows]
      .reverse()
      .map((r) => ({ date: r.trade_date_ny, equity: num(r.equity_close_usd) }))
      .filter((p): p is { date: string; equity: number } => p.equity != null);
  }, [rows]);

  if (points.length < 2) {
    return <div className="muted">Not enough equity history to chart.</div>;
  }

  const W = 720;
  const H = 180;
  const PAD = 8;
  const values = points.map((p) => p.equity);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const x = (i: number) => PAD + (i * (W - 2 * PAD)) / (points.length - 1);
  const y = (v: number) => PAD + (1 - (v - min) / span) * (H - 2 * PAD);
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.equity).toFixed(1)}`).join(" ");
  const last = points[points.length - 1];
  const first = points[0];
  const up = last.equity >= first.equity;

  return (
    <div className="chartWrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="sparkline" role="img" aria-label="Equity curve">
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} className="axis" />
        <path d={path} className={up ? "line pos" : "line neg"} fill="none" />
      </svg>
      <div className="chartMeta muted">
        <span>{first.date}: {usd(first.equity)}</span>
        <span>{last.date}: {usd(last.equity)}</span>
      </div>
    </div>
  );
}

export default function PnlPage() {
  const [data, setData] = useState<PnlResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    fetchPnlV2(ctrl.signal)
      .then((payload) => {
        setData(payload);
        setLoading(false);
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof ApiHttpError && err.status === 401) {
          window.location.assign("/signin");
          return;
        }
        setError(String((err as Error)?.message || err));
        setLoading(false);
      });
    return () => ctrl.abort();
  }, []);

  if (loading) return <div className="muted">Loading P&amp;L record…</div>;
  if (error) return <div className="banner error">Failed to load P&amp;L: {error}</div>;
  if (!data) return null;

  const t = data.totals;

  return (
    <section className="view">
      <header className="viewHeader">
        <h2>P&amp;L — the honest record</h2>
        <p className="muted">
          Closed-trade attribution for the paper trader. The trading edge concluded a rigorous negative;
          negative P&amp;L is expected and shown plainly.
        </p>
        <p className="muted">
          {t.first_closed_ts ? t.first_closed_ts.slice(0, 10) : "—"} → {t.last_closed_ts ? t.last_closed_ts.slice(0, 10) : "—"}
        </p>
      </header>

      <div className="kpiGrid">
        <div className="kpi">
          <div className="kpiLabel">Total P&amp;L</div>
          <div className={`kpiValue ${pnlClass(t.total_pnl_usd)}`}>{usd(t.total_pnl_usd)}</div>
        </div>
        <div className="kpi">
          <div className="kpiLabel">Win rate</div>
          <div className="kpiValue mono">
            {pct(t.win_rate)}
            <span className="kpiSub"> {int(t.wins)}W / {int(t.losses)}L</span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpiLabel">Avg P&amp;L</div>
          <div className="kpiValue mono">{bps(t.avg_pnl_bps)}</div>
        </div>
        <div className="kpi">
          <div className="kpiLabel">Trades</div>
          <div className="kpiValue mono">{int(t.trades)}</div>
        </div>
        <div className="kpi">
          <div className="kpiLabel">Total fees</div>
          <div className="kpiValue mono">{usd(t.total_fees_usd)}</div>
        </div>
        <div className="kpi">
          <div className="kpiLabel">Avg slippage</div>
          <div className="kpiValue mono">{bps(t.avg_slippage_bps)}</div>
        </div>
      </div>

      <h3>Equity curve</h3>
      <EquitySparkline rows={data.equity_curve} />

      <h3>By symbol</h3>
      <BreakdownTable
        cols={["symbol", "trades", "pnl_usd", "win_rate", "avg_pnl_bps"]}
        rows={data.by_symbol.map((r: PnlBySymbolRow) => ({
          key: r.symbol,
          symbol: r.symbol,
          trades: r.trades,
          pnl_usd: r.pnl_usd,
          win_rate: r.win_rate,
          avg_pnl_bps: r.avg_pnl_bps,
        }))}
      />

      <h3>By strategy / model</h3>
      <BreakdownTable
        cols={["strategy", "trades", "pnl_usd", "win_rate", "avg_pnl_bps"]}
        rows={data.by_strategy.map((r: PnlByStrategyRow, i) => ({
          key: `${r.strategy_id ?? "—"}:${r.model_name ?? "—"}:${i}`,
          strategy: `${r.strategy_id ?? "—"}${r.model_name ? ` · ${r.model_name}` : ""}`,
          trades: r.trades,
          pnl_usd: r.pnl_usd,
          win_rate: r.win_rate,
          avg_pnl_bps: r.avg_pnl_bps,
        }))}
      />

      <h3>By horizon</h3>
      <BreakdownTable
        cols={["horizon", "trades", "pnl_usd", "win_rate", "avg_pnl_bps"]}
        rows={data.by_horizon.map((r: PnlByHorizonRow, i) => ({
          key: `${r.horizon_min ?? "—"}:${i}`,
          horizon: r.horizon_min == null ? "—" : `${int(r.horizon_min)} min`,
          trades: r.trades,
          pnl_usd: r.pnl_usd,
          win_rate: r.win_rate,
          avg_pnl_bps: r.avg_pnl_bps,
        }))}
      />
    </section>
  );
}

type BreakdownRow = {
  key: string;
  [col: string]: string | number | null;
};

function BreakdownTable({ cols, rows }: { cols: string[]; rows: BreakdownRow[] }) {
  const labelCol = cols[0];
  const headerLabel: Record<string, string> = {
    symbol: "symbol",
    strategy: "strategy · model",
    horizon: "horizon",
    trades: "trades",
    pnl_usd: "P&L",
    win_rate: "win rate",
    avg_pnl_bps: "avg bps",
  };
  return (
    <table className="dataTable">
      <thead>
        <tr>
          {cols.map((c) => (
            <th key={c} className={c === labelCol ? undefined : "numCol"}>
              {headerLabel[c] ?? c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={cols.length} className="muted">No closed trades.</td>
          </tr>
        ) : (
          rows.map((r) => (
            <tr key={r.key}>
              {cols.map((c) => {
                if (c === labelCol) {
                  return (
                    <td key={c} className="mono">
                      {r[c] == null ? "—" : String(r[c])}
                    </td>
                  );
                }
                if (c === "pnl_usd") {
                  return (
                    <td key={c} className={`numCol ${pnlClass(r[c])}`}>
                      {usd(r[c])}
                    </td>
                  );
                }
                if (c === "win_rate") {
                  return (
                    <td key={c} className="numCol mono">
                      {pct(r[c])}
                    </td>
                  );
                }
                if (c === "avg_pnl_bps") {
                  return (
                    <td key={c} className="numCol mono">
                      {bps(r[c])}
                    </td>
                  );
                }
                return (
                  <td key={c} className="numCol mono">
                    {int(r[c])}
                  </td>
                );
              })}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}
